/**
 * Admin Settings Page
 *
 * Security Design (for viva):
 * - This page manages SYSTEM-LEVEL configuration and admin preferences
 * - Admins CANNOT access user files or encryption keys
 * - Admins CANNOT decrypt any data — zero-knowledge is enforced
 * - Admins CANNOT control user passwords or reset accounts
 * - Audit log retention and alert thresholds are governance tools
 * - All sensitive setting changes are logged for accountability
 *
 * Why certain settings are NOT here:
 * - User file access: zero-knowledge prevents admin from decrypting
 * - Key management: server never possesses encryption keys
 * - Password controls: passwords are user-owned and bcrypt-hashed
 */

import React, { useState, useEffect } from 'react';
import { getAdminDashboard, getAdminSecurityPolicy, updateAdminSecurityPolicy } from '../services/api';

// Admin settings stored per-admin in localStorage
function loadAdminSettings(email) {
  try {
    const key = email ? `cdeaas_settings_${email}` : 'cdeaas_settings';
    const saved = JSON.parse(localStorage.getItem(key) || '{}');
    return {
      darkMode: true, sessionTimeout: 15, itemsPerPage: 15, dateFormat: '24h',
      displayName: '', notifications: true, exportFormat: 'pdf', compactMode: false,
      // Admin-specific settings
      auditRetentionDays: 90,
      alertThreshold: 5,        // failed OTP attempts before alert
      autoLockThreshold: 10,    // failed attempts before user lock suggestion
      alertEmailEnabled: false, // email notifications for critical alerts
      showSystemHealth: true,
      ...saved
    };
  } catch {
    return { darkMode: true, sessionTimeout: 15, itemsPerPage: 15, dateFormat: '24h', displayName: '', notifications: true, exportFormat: 'pdf', compactMode: false, auditRetentionDays: 90, alertThreshold: 5, autoLockThreshold: 10, alertEmailEnabled: false, showSystemHealth: true };
  }
}

function saveAdminSettings(email, settings) {
  const key = email ? `cdeaas_settings_${email}` : 'cdeaas_settings';
  localStorage.setItem(key, JSON.stringify(settings));
  window.dispatchEvent(new Event('settingsChanged'));
}

function AdminSettingsPage({ user }) {
  const [cfg, setCfg] = useState(() => loadAdminSettings(user?.email));
  const [nameEdit, setNameEdit] = useState(cfg.displayName || '');
  const [saved, setSaved] = useState(false);
  const [systemInfo, setSystemInfo] = useState(null);

  useEffect(() => {
    getAdminDashboard().then(setSystemInfo).catch(() => {});
    getAdminSecurityPolicy()
      .then((policy) => {
        if (!policy || typeof policy !== 'object') return;
        setCfg((prev) => {
          const next = {
            ...prev,
            alertThreshold: Number(policy.alert_threshold ?? prev.alertThreshold),
            autoLockThreshold: Number(policy.auto_lock_threshold ?? prev.autoLockThreshold),
          };
          saveAdminSettings(user?.email, next);
          return next;
        });
      })
      .catch(() => {});
  }, [user?.email]);

  const update = (key, value) => {
    const next = { ...cfg, [key]: value };
    setCfg(next);
    saveAdminSettings(user?.email, next);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const updateSecurityPolicy = async (key, value) => {
    const next = { ...cfg, [key]: value };
    setCfg(next);
    saveAdminSettings(user?.email, next);
    try {
      await updateAdminSecurityPolicy(next.alertThreshold, next.autoLockThreshold);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // Keep UI responsive; old value can still be recovered on next reload.
    }
  };

  const Toggle = ({ on, onChange }) => (
    <button onClick={() => onChange(!on)} style={{
      width: 44, height: 24, borderRadius: 14, border: 'none', cursor: 'pointer',
      background: on ? 'var(--blue)' : 'var(--gray-300)', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
    }}>
      <span style={{
        position: 'absolute', top: 2, left: on ? 22 : 2, width: 20, height: 20,
        borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </button>
  );

  return (
    <div className="page-container admin-page-shell">
      <h2 style={st.pageTitle}>Admin Settings</h2>
      <p style={st.subtitle}>System configuration and administrative preferences.</p>

      {/* ── Admin Profile ── */}
      <div style={st.card}>
        <div style={st.sTitle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          Admin Profile
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Email Address</div><div style={st.sub}>Admin login identity</div></div>
          <span style={st.val}>{user?.email}</span>
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Role</div><div style={st.sub}>Highest privilege level</div></div>
          <span className="security-badge red">ADMINISTRATOR</span>
        </div>
        <div style={st.rowLast}>
          <div><div style={st.lbl}>Display Name</div><div style={st.sub}>Shown in the admin panel header</div></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input style={st.input} value={nameEdit}
              onChange={e => setNameEdit(e.target.value)}
              onBlur={() => { if (nameEdit !== cfg.displayName) update('displayName', nameEdit); }}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
              placeholder="Enter admin name" />
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Auto-saved</span>
          </div>
        </div>
      </div>

      {/* ── Audit & Governance ──
        Why these settings exist:
        Audit log retention allows governance compliance.
        Alert thresholds control when security alerts fire.
        These do NOT affect user data or encryption. */}
      <div style={st.card}>
        <div style={st.sTitle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
          </svg>
          Audit &amp; Governance
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Audit Log Retention</div><div style={st.sub}>How long audit records are kept before archival</div></div>
          <select style={st.select} value={cfg.auditRetentionDays} onChange={e => update('auditRetentionDays', Number(e.target.value))}>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days (default)</option>
            <option value={180}>180 days</option>
            <option value={365}>1 year</option>
          </select>
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Items Per Page</div><div style={st.sub}>Number of rows in audit log and alert tables</div></div>
          <select style={st.select} value={cfg.itemsPerPage} onChange={e => update('itemsPerPage', Number(e.target.value))}>
            <option value={10}>10 items</option>
            <option value={15}>15 items (default)</option>
            <option value={25}>25 items</option>
            <option value={50}>50 items</option>
          </select>
        </div>
        <div style={st.rowLast}>
          <div><div style={st.lbl}>Default Export Format</div><div style={st.sub}>Preferred format for audit log / alert exports</div></div>
          <select style={st.select} value={cfg.exportFormat} onChange={e => update('exportFormat', e.target.value)}>
            <option value="pdf">PDF Document</option>
            <option value="excel">Excel Spreadsheet</option>
          </select>
        </div>
      </div>

      {/* ── Security Monitoring ── */}
      <div style={st.card}>
        <div style={st.sTitle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          Security Monitoring
        </div>
        <div style={{ padding: '10px 14px', marginBottom: 16, background: 'var(--bg-elevated)', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-primary)' }}>What counts as a failed attempt here?</strong>
          <br /><br />
          A <strong>failed OTP attempt</strong> is when a user enters a wrong or expired one-time code (for example during file decryption, approving access, or login). Each failure is recorded in the audit log.
          <br /><br />
          <strong>Alert threshold:</strong> After this many failed attempts, the system raises a security alert for admins (shown under Security Alerts). You can review the pattern and act if it looks suspicious.
          <br /><br />
          <strong>Auto-lock suggestion:</strong> After this many failed attempts, the user is marked <strong>flagged</strong> under User Access. You may suspend the account if your policy requires it.
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Alert Threshold (Failed OTP)</div><div style={st.sub}>Number of failed OTP attempts before a security alert is raised for admins</div></div>
          <select style={st.select} value={cfg.alertThreshold} onChange={e => updateSecurityPolicy('alertThreshold', Number(e.target.value))}>
            <option value={3}>3 attempts</option>
            <option value={5}>5 attempts (default)</option>
            <option value={10}>10 attempts</option>
            <option value={15}>15 attempts</option>
          </select>
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Auto-Lock Suggestion Threshold</div><div style={st.sub}>Failed attempts before the user is marked flagged for admin review</div></div>
          <select style={st.select} value={cfg.autoLockThreshold} onChange={e => updateSecurityPolicy('autoLockThreshold', Number(e.target.value))}>
            <option value={5}>5 attempts</option>
            <option value={10}>10 attempts (default)</option>
            <option value={20}>20 attempts</option>
          </select>
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Session Timeout</div><div style={st.sub}>Admin auto-logout after inactivity</div></div>
          <select style={st.select} value={cfg.sessionTimeout} onChange={e => update('sessionTimeout', Number(e.target.value))}>
            <option value={5}>5 minutes</option>
            <option value={10}>10 minutes</option>
            <option value={15}>15 minutes (default)</option>
            <option value={30}>30 minutes</option>
            <option value={60}>1 hour</option>
          </select>
        </div>
        <div style={st.rowLast}>
          <div><div style={st.lbl}>Zero-Knowledge Architecture</div><div style={st.sub}>Server never processes plaintext or encryption keys</div></div>
          <span className="security-badge green">ENFORCED</span>
        </div>
      </div>

      {/* Admin Security Limitation Notice */}
      <div style={st.secNotice}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
        <div>
          <strong>Admin Limitations (Zero-Knowledge):</strong> Administrators cannot access, decrypt, or view user file contents. Encryption keys are derived client-side and never transmitted to the server. This is by design — even with full database access, the admin cannot read encrypted files. This separation is critical for the JOSMY security model.
        </div>
      </div>

      {/* ── Notification Preferences ── */}
      <div style={st.card}>
        <div style={st.sTitle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--yellow)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          Notification Preferences
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Security Alert Badges</div><div style={st.sub}>Show red badge on sidebar when unreviewed alerts exist</div></div>
          <Toggle on={cfg.notifications} onChange={v => update('notifications', v)} />
        </div>
        <div style={st.rowLast}>
          <div><div style={st.lbl}>Show System Health</div><div style={st.sub}>Display system health indicators on admin dashboard</div></div>
          <Toggle on={cfg.showSystemHealth} onChange={v => update('showSystemHealth', v)} />
        </div>
      </div>

      {/* ── Appearance ── */}
      <div style={st.card}>
        <div style={st.sTitle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
          Appearance
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Dark Mode</div><div style={st.sub}>Switch between light and dark theme</div></div>
          <Toggle on={cfg.darkMode} onChange={v => update('darkMode', v)} />
        </div>
        <div style={st.rowLast}>
          <div><div style={st.lbl}>Date/Time Format</div><div style={st.sub}>How timestamps are displayed across admin panel</div></div>
          <select style={st.select} value={cfg.dateFormat} onChange={e => update('dateFormat', e.target.value)}>
            <option value="24h">24-hour (14:30)</option>
            <option value="12h">12-hour (2:30 PM)</option>
          </select>
        </div>
      </div>

      {/* ── System Health Overview ── */}
      {cfg.showSystemHealth && (
        <div style={st.card}>
          <div style={st.sTitle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
            System Health
          </div>
          <div style={st.row}>
            <div style={st.lbl}>Total Users</div>
            <span style={st.val}>
              {Number.isFinite(Number(systemInfo?.total_users))
                ? Number(systemInfo.total_users)
                : (Number.isFinite(Number(systemInfo?.totalUsers)) ? Number(systemInfo.totalUsers) : 0)}
            </span>
          </div>
          <div style={st.row}>
            <div style={st.lbl}>Total Encrypted Files</div>
            <span style={st.val}>
              {Number.isFinite(Number(systemInfo?.total_files))
                ? Number(systemInfo.total_files)
                : (Number.isFinite(Number(systemInfo?.totalFiles)) ? Number(systemInfo.totalFiles) : 0)}
            </span>
          </div>
          <div style={st.row}>
            <div style={st.lbl}>Storage Used</div>
            <span style={st.val}>
              {Number.isFinite(Number(systemInfo?.total_storage_mb))
                ? `${Number(systemInfo.total_storage_mb).toFixed(1)} MB`
                : (Number.isFinite(Number(systemInfo?.storage_used))
                    ? `${(Number(systemInfo.storage_used) / 1024 / 1024).toFixed(2)} MB`
                    : '0.0 MB')}
            </span>
          </div>
          <div style={st.row}>
            <div style={st.lbl}>Platform</div>
            <span style={st.val}>JOSMY v1.0</span>
          </div>
          <div style={st.row}>
            <div style={st.lbl}>Backend</div>
            <span style={st.val}>FastAPI + SQL Database</span>
          </div>
          <div style={st.rowLast}>
            <div style={st.lbl}>Frontend</div>
            <span style={st.val}>React 18</span>
          </div>
        </div>
      )}

      {saved && (
        <div style={st.toast}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          Settings saved
        </div>
      )}
    </div>
  );
}

const st = {
  pageTitle: {
    fontSize: 22, fontWeight: 900, margin: '0 0 6px', letterSpacing: '-0.4px',
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 20px', fontWeight: 500 },
  card: {
    background: 'var(--surface-85)', borderRadius: 22, border: '1.5px solid rgba(99,102,241,0.08)',
    padding: 28, marginBottom: 20, boxShadow: '0 4px 20px rgba(99,102,241,0.06)',
    backdropFilter: 'blur(12px)',
  },
  sTitle: { fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10, letterSpacing: '-0.2px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid rgba(99,102,241,0.06)', gap: 16, flexWrap: 'wrap' },
  rowLast: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', gap: 16, flexWrap: 'wrap' },
  lbl: { fontSize: 13, fontWeight: 700, color: 'var(--text-body)' },
  sub: { fontSize: 11, color: 'var(--text-muted)', marginTop: 3, fontWeight: 500 },
  val: { fontSize: 14, color: 'var(--text-primary)', fontWeight: 600 },
  select: { padding: '10px 14px', border: '1.5px solid rgba(99,102,241,0.1)', borderRadius: 14, fontSize: 14, backgroundColor: 'var(--surface-90)', color: 'var(--text-body)', cursor: 'pointer', minWidth: 150, transition: 'all 0.2s ease' },
  input: { padding: '10px 14px', border: '1.5px solid rgba(99,102,241,0.1)', borderRadius: 14, fontSize: 14, backgroundColor: 'var(--surface-90)', color: 'var(--text-body)', width: 210, transition: 'all 0.2s ease' },
  secNotice: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 20px', background: 'linear-gradient(135deg, rgba(239,68,68,0.06), rgba(220,38,38,0.03))', border: '1.5px solid rgba(239,68,68,0.1)', borderRadius: 18, marginBottom: 20, fontSize: 13, color: 'var(--text-faint)', lineHeight: 1.5 },
  toast: { position: 'fixed', bottom: 24, right: 24, background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff', padding: '12px 22px', borderRadius: 14, fontSize: 13, fontWeight: 700, boxShadow: '0 6px 20px rgba(34,197,94,0.3)', zIndex: 999, display: 'flex', alignItems: 'center', gap: 8 },
};

export default AdminSettingsPage;
